import React from "react";
import { action, computed, makeObservable, observable } from "mobx";
import ScrollManager from "window-scroll-manager";
import { WindowManager } from "react-window-decorators";

import {
  mobileNavWidth,
  primaryNavHeight,
  secondaryNavHeight,
} from "../utils/Settings";
import { isSchoolBlocksApp } from "../utils/SchoolBlocksUtilities";

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree. An additional grant
 * of patent rights can be found in the PATENTS file in the same directory.
 *
 * @providesModule ExecutionEnvironment
 */

const canUseDOM = !!(
  typeof window !== "undefined" &&
  window.document &&
  window.document.createElement
);

function isPWA() {
  // For iOS
  // @ts-ignore
  if (window.navigator.standalone) return true;

  // For Android
  if (window.matchMedia("(display-mode: standalone)").matches) return true;

  // If neither is true, it's not installed
  return false;
}

/**
 * Simple, lightweight module assisting with the detection and context of
 * Worker. Helps avoid circular dependencies and allows code to reason about
 * whether or not they are in a Worker, even if they never include the main
 * `ReactWorker` dependency.
 */
export const ExecutionEnvironment = {
  canUseDOM: canUseDOM,

  canUseWorkers: typeof Worker !== "undefined",

  canUseEventListeners: canUseDOM && !!window.addEventListener,

  canUseViewport: canUseDOM && !!window.screen,

  isInWorker: !canUseDOM, // For now, this is true - might change in the future.
};

export const isServerComponent = typeof React.useState === "undefined";

export const enum IView {
  MAP = "map",
  ALPHA = "alpha",
  DEFAULT = "default",
  CLASSROOM = "classroom",
  PAGE = "page",
  FOLLOWING = "following",
  FEED = "feed",
  SEARCH = "search",
  ERROR = "error",
  LOGIN = "login",
  USER_SETTINGS = "userSettings",
  ORGANIZATION_SETTINGS = "organizationSettings",
  OFFLINE = "offline",
  CALENDAR = "calendar",
  MYCALENDAR = "myCalendar",
  ORGANIZATION_PAGE = "organization",
  SCHOOLFEED_PREMIUM = "schoolfeedPremium",
  SPLAINER_PAGE = "splainer",
  NOTIFICATION = "notification",
  CLAIM = "claim",
  DASHBOARD = "dashboard",
  CONTENT = "content",
  LEADERBOARD = "leaderboard",
  SEEHOWITWORKS = "seehowitworks",
  CONVERSATION_ANALYTICS = "conversationanalytics",
  MESSAGING = "messaging",
  LIST = "list",
  MAGIC_LINK_ERROR = "magicLinkError",
  CLEVERFEED_DIRECTORY = "cleverfeedDirectory",
}

export type IInterfaceStoreInitData = {
  currentFullUrl: SoprisAppsProps["pageProps"]["currentFullUrl"];
  userAgent: SoprisAppsProps["pageProps"]["userAgent"];
  view: SoprisAppsProps["pageProps"]["view"];
  stableRequestId: SoprisAppsProps["pageProps"]["stableRequestId"];
  parsedUserAgent: SoprisAppsProps["pageProps"]["parsedUserAgent"];
};

export default class InterfaceStore {
  scrollPosition = 0;
  dimensions = {
    width: 0,
    height: 0,
  };
  width = 0;
  height = 0;
  breakpoint = "break-point-xs";
  orientation = null;
  userAgent: string = "";
  parsedUserAgent: SoprisAppsProps["pageProps"]["parsedUserAgent"] = {
    isBot: false,
    browser: { name: undefined, version: undefined },
    device: { model: undefined, type: undefined, vendor: undefined },
    engine: { name: undefined, version: undefined },
    os: { name: undefined, version: undefined },
    cpu: { architecture: undefined },
  };
  isMobileAccountMenuOpen = false;
  isMobileNavMenuOpen = false;
  isSchoolBlocks = false;
  isTouchDevice = false;
  currentFullUrl: string | null = null;
  userMenuOpen = false;
  isEditingPage = false;
  schoolFeedWidgetOpen = false;
  view: IView = IView.DEFAULT;
  isPWA = false;
  zoomValue = 100;
  initialized = false;

  public ExecutionEnvironment;
  public scrollManager;
  public windowManager;
  public stableRequestId;

  constructor(initData: IInterfaceStoreInitData | undefined) {
    this.initialize(initData);
    // window manager should be used for resize
    // scroll manager should be used for scroll
    makeObservable(this, {
      scrollPosition: observable,
      breakpoint: observable,
      width: observable,
      height: observable,
      userMenuOpen: observable,
      toggleUserMenu: action,
      orientation: observable,
      isTouchDevice: observable,
      currentFullUrl: observable,
      userAgent: observable,
      setCurrentFullUrl: action,
      handleBrowserScroll: action,
      setBreakpoint: action,
      handleWindowResize: action,
      isEditingPage: observable,
      isSchoolBlocks: observable,
      toggleEditingPage: action,
      startEditingPage: action,
      stopEditingPage: action,
      isMobileAccountMenuOpen: observable,
      isMobileNavMenuOpen: observable,
      toggleMobileAccountMenu: action,
      toggleMobileNavMenu: action,
      closeAllMenus: action,
      setUserAgent: action,
      schoolFeedWidgetOpen: observable,
      toggleSchoolFeedWidgetOpen: action,
      view: observable,
      setView: action,
      zoomValue: observable,
      setZoomValue: action,
      isMainGrid: computed,
    });

    this.ExecutionEnvironment = ExecutionEnvironment;
    this.isSchoolBlocks = isSchoolBlocksApp();

    this.scrollManager = new ScrollManager();
    this.windowManager = new WindowManager(undefined, undefined);
    if (this.ExecutionEnvironment.canUseDOM) {
      this.isTouchDevice = !!(
        "ontouchstart" in window || navigator?.maxTouchPoints
      );
      this.isPWA = isPWA();

      window.addEventListener("window-scroll", this.handleBrowserScroll, {
        passive: true,
      });
      this.scrollManager.handleScroll();

      window.addEventListener &&
        window.addEventListener("window-resize", this.handleWindowResize, {
          passive: true,
        });
      window.addEventListener("route", () => {
        this.setCurrentFullUrl(window.location.href);
      });
      // set the dimensions initially.
      this.dimensions = this.windowManager.getDimensions();
      this.setBreakpoint();
      this.setZoomValue();

      // establish the window resize event listener
      this.windowManager.handleResize();
    }
  }

  initialize = (initData: IInterfaceStoreInitData | undefined) => {
    if (initData) {
      this.setRandomSeed(initData.stableRequestId);
      this.setCurrentFullUrl(initData.currentFullUrl);
      this.setUserAgent(initData.userAgent);
      this.setParsedUserAgent(initData.parsedUserAgent);
      this.setView(initData.view || IView.ERROR);
      this.initialized = true;
    }
  };

  serialize: () => IInterfaceStoreInitData = () => {
    return {
      userAgent: this.userAgent,
      parsedUserAgent: this.parsedUserAgent,
      currentFullUrl: this.currentFullUrl || "",
      view: this.view,
      stableRequestId: this.stableRequestId,
    };
  };

  setRandomSeed(stableRequestId) {
    this.stableRequestId = stableRequestId;
  }

  setParsedUserAgent(parsedUserAgent) {
    this.parsedUserAgent = parsedUserAgent;
  }

  setUserAgent(userAgent) {
    // to be run on the server so we can take a good guess at the number of grid columns we should display on initial render
    // https://github.com/google/closure-library/blob/master/closure/goog/labs/useragent/device.js
    this.userAgent = userAgent;

    if (
      /iPod/.exec(userAgent) ||
      /iPhone/.exec(userAgent) ||
      /Android/.exec(userAgent) ||
      /IEMobile/.exec(userAgent)
    ) {
      this.breakpoint = "break-point-xs";
    } else if (/iPad/.exec(userAgent) || /Silk/.exec(userAgent)) {
      this.breakpoint = "break-point-sm";
    } else {
      this.breakpoint = "break-point-lg";
    }
  }

  setView(view: IView) {
    this.view = view;
  }

  get isMainGrid(): boolean {
    return this.view === IView.DEFAULT || this.view === IView.CLASSROOM;
  }

  toggleSchoolFeedWidgetOpen() {
    this.schoolFeedWidgetOpen = !this.schoolFeedWidgetOpen;
  }

  setCurrentFullUrl(url) {
    this.currentFullUrl = url;
  }

  closeAllMenus = () => {
    this.isMobileAccountMenuOpen = false;
    this.isMobileNavMenuOpen = false;
  };

  toggleUserMenu = () => {
    this.userMenuOpen = !this.userMenuOpen;
  };

  toggleEditingPage = () => {
    this.isEditingPage = !this.isEditingPage;
  };

  toggleMobileAccountMenu = () => {
    this.isMobileAccountMenuOpen = !this.isMobileAccountMenuOpen;
  };

  toggleMobileNavMenu = () => {
    this.isMobileNavMenuOpen = !this.isMobileNavMenuOpen;
  };

  startEditingPage = () => {
    this.isEditingPage = true;
  };

  stopEditingPage = () => {
    this.isEditingPage = false;
  };

  handleBrowserScroll = (e) => {
    this.scrollPosition = e.detail.scrollPosition;
  };

  setBreakpoint() {
    let bodyStyle: undefined | CSSStyleDeclaration = undefined;
    try {
      bodyStyle = window.getComputedStyle(document.body, ":before");
    } catch (e) {
      // pass.  If we can't get the computed style, then we need to move on and let the resize handler pick it up
      console.error(e);
    }

    if (bodyStyle !== undefined) {
      this.breakpoint = bodyStyle.getPropertyValue("content").replace(/"/g, "");
    }
  }

  handleWindowResize = (e) => {
    const doUpdate = action(() => {
      this.orientation = e.detail.orientation;
      this.dimensions = e.detail.dimensions;
      this.width = e.detail.dimensions.width;
      this.height = e.detail.dimensions.height;

      // Maybe we use the library's breakpoint functionality in the future.  For now, we have our own
      // that comes in through our SCSS stuff so we define it *one time* and can use it in CSS and JS.
      //this.breakpoint = e.detail.breakpoint;
      this.setBreakpoint();
      this.setZoomValue();
    });

    if (requestAnimationFrame) {
      requestAnimationFrame(doUpdate);
    } else {
      doUpdate();
    }
  };

  setZoomValue() {
    this.zoomValue =
      Math.round((((window.outerWidth - 10) / window.innerWidth) * 100) / 5) *
      5;
  }

  scrollToElement = (selector: string, scrollOptions = {}) => {
    if (
      this.ExecutionEnvironment.canUseDOM &&
      typeof document !== "undefined"
    ) {
      const blockToScroll = document.querySelector(selector);

      if (blockToScroll) {
        const rect = blockToScroll.getBoundingClientRect();
        const navOffset =
          this.dimensions.width < mobileNavWidth
            ? 0 // Mobile: nav is at bottom, no offset needed
            : primaryNavHeight + secondaryNavHeight; // Desktop: both navs are sticky at top
        window.scroll({
          top: this.scrollPosition + rect.top - navOffset,
          behavior: "smooth",
          ...scrollOptions,
        });
      }
    }
  };
}
